import { Box } from '@mui/material';
import { RootState, useAppSelector } from 'common';
import { RecipeHeader } from 'common/src/api/models/recipe-header.models';
import { FC, useEffect, useRef } from 'react';

import {
  CfDataGridData,
  CfDataGridDataRequest,
  CfDataGridProps,
  CfRecomputeRowHeightsRefCallback,
} from '../../../../cf-ui/DataGrid/CfDataGridProps';
import { CfDataGridContainer } from '../../../../cf-ui/DataGridContainer/CfDataGridContainer';
import { CfSearchInput } from '../../../../cf-ui/SearchInput/CfSearchInput';
import { createTableViewDefinitions } from './RecipeContainedGrid.table';

type RecipeGridProps = {
  id: string;
  handleFilter: (value: string) => void;
  filterText: string;
  recipeHeaders: RecipeHeader[];
  renderActionComponentLeft?: (recipe: RecipeHeader) => React.ReactNode;
  renderActionComponentRight?: (recipe: RecipeHeader) => React.ReactNode;
};

export const RecipeContainedGrid: FC<RecipeGridProps> = (props: RecipeGridProps) => {
  const isRecipeHeadersLoading = useAppSelector((s: RootState) => s.recipes.isRecipeHeadersLoading);

  const recomputeRowHeightsRef = useRef<(() => void) | null>(null);
  const setRecomputeHeightsRef: CfRecomputeRowHeightsRefCallback = (recomputeHeights: (() => void) | null) => {
    recomputeRowHeightsRef.current = recomputeHeights;
  };

  const handleFilter = (request: CfDataGridDataRequest) => {
    props.handleFilter?.(request.filterText || '');
  };

  useEffect(() => {
    // recompute row heights after opening the modal
    recomputeRowHeightsRef.current?.();
  }, []);

  const size = isRecipeHeadersLoading ? 8 : props.recipeHeaders.length;
  const gridData: CfDataGridData<RecipeHeader> = {
    pages: [
      {
        items: props.recipeHeaders,
        pageIndex: 0,
        pageSize: size,
        isLoading: isRecipeHeadersLoading,
      },
    ],
    totalItemCount: size,
    totalPageCount: 1,
    pagingMode: 'none',
  };
  const dataGridProps: CfDataGridProps<RecipeHeader> = {
    id: props.id,
    tableView: createTableViewDefinitions(
      setRecomputeHeightsRef,
      props.renderActionComponentLeft,
      props.renderActionComponentRight
    ),
    data: gridData as CfDataGridData<RecipeHeader>,
    selectId: (recipeHeader: RecipeHeader) => recipeHeader.RecipeHeaderId + (recipeHeader.IsCollapsed ?? false), // work around, handling collapsing category row heights
    onDataRequested: handleFilter,
    noItemsMessage: 'No recipes found',
    noItemsMessageSx: { mt: 0.5 },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <CfSearchInput
        sx={{ width: '100%', mb: 2 }}
        autoFocus
        onSubmit={(filterText?: string) => {
          if (filterText !== props.filterText) {
            props.handleFilter(filterText ?? '');
          }
        }}
        onClear={() => props.handleFilter('')}
        placeholder='Search recipes'
      />
      <CfDataGridContainer
        sx={{
          '& .MuiCardContent-root': {
            padding: '0 !important',
          },
        }}
        dataGridProps={dataGridProps}
      />
    </Box>
  );
};
